import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import API_BASE_URL from "../../config/api";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Divider,
} from "@mui/material";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { toast } from "react-toastify";

export default function MySalary() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [salaryInfo, setSalaryInfo] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Fetch salary information with staff type
      const salaryResponse = await fetch(
        `${API_BASE_URL}/api/staff/my-salary-with-type`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (salaryResponse.ok) {
        const salaryData = await salaryResponse.json();
        setSalaryInfo(salaryData);
      } else {
        console.error("Failed to fetch salary:", await salaryResponse.text());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Generate all months from joining date to current month
  const generateAllMonths = () => {
    const months = [];
    const currentDate = new Date();
    
    // If no salary info, only show current month
    if (!salaryInfo || !salaryInfo.joiningDate) {
      const monthYear = currentDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      return [{
        monthYear,
        month: currentDate.getMonth() + 1,
        year: currentDate.getFullYear(),
      }];
    }
    
    // Start from joining month
    const startDate = new Date(salaryInfo.joiningDate);
    let iterDate = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    
    while (iterDate <= endDate) {
      const month = iterDate.getMonth() + 1; // 1-12
      const year = iterDate.getFullYear();
      const monthYear = iterDate.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      });
      
      months.push({
        monthYear,
        month,
        year,
      });
      
      // Move to next month
      iterDate.setMonth(iterDate.getMonth() + 1);
    }
    
    // Return in reverse order (most recent first)
    return months.reverse();
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 0.5, sm: 2, md: 4 } }}>
        {/* Header */}
        <Box sx={{ mb: { xs: 2, md: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 600,
              color: "#8B7355",
              fontSize: { xs: "1.5rem", sm: "2rem", md: "2.125rem" },
              textAlign: { xs: "center", sm: "left" },
            }}
          >
            My Salary
          </Typography>
        </Box>

        {/* Salary Records */}
        <Card sx={{ 
          boxShadow: 3,
          bgcolor: "#2d2d2d",
          border: "1px solid #444"
        }}>
          <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Typography
              variant="h5"
              sx={{ 
                fontWeight: 600, 
                mb: 2, 
                color: "#C4A574",
                fontSize: { xs: "1.25rem", sm: "1.5rem" }
              }}
            >
              Salary Records
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "#555" }} />

            <Box>
              {generateAllMonths().length === 0 ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                  <Typography 
                    color="#9CA3AF" 
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    No salary records found
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 1, sm: 2 } }}>
                  {generateAllMonths().map((data) => (
                    <Card
                      key={data.monthYear}
                      sx={{
                        bgcolor: "#2d2d2d",
                        boxShadow: 2,
                        border: "1px solid #444",
                        "&:hover": { 
                          boxShadow: 4,
                          transform: "translateY(-2px)",
                          transition: "all 0.2s ease-in-out"
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: { xs: "flex-start", sm: "center" },
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 2, sm: 1 },
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 600, 
                                color: "#C4A574",
                                fontSize: { xs: "1rem", sm: "1.25rem" }
                              }}
                            >
                              {data.monthYear}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                color: "#9CA3AF", 
                                mt: 0.5,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" }
                              }}
                            >
                              Click to view detailed salary breakdown
                            </Typography>
                          </Box>
                          <Box sx={{ 
                            display: "flex", 
                            gap: { xs: 1, sm: 2 },
                            width: { xs: "100%", sm: "auto" }
                          }}>
                            <Button
                              variant="contained"
                              startIcon={<AttachMoneyIcon />}
                              onClick={() => navigate(`/my-salary-details/${data.month}/${data.year}`)}
                              sx={{
                                bgcolor: "#C4A574",
                                color: "white",
                                fontWeight: 600,
                                px: { xs: 2, sm: 3 },
                                py: { xs: 1, sm: 1.5 },
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                flex: { xs: 1, sm: "none" },
                                "&:hover": { 
                                  bgcolor: "#A68B5B",
                                  transform: "scale(1.02)"
                                },
                              }}
                            >
                              View Details
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
